import { AxiosResponse } from 'axios';
import { Question } from '@/types/api';
import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

export type GetQuestionProps = {
  questionId: number | string;
};

export type EditRotatingQuestionProps = {
  question: Partial<Question>;
};

export type CreateRotatingQuestionProps = {
  question: Omit<Question, 'id' | 'createdAt' | 'updatedAt' | 'isActive'>;
};

export type DeleteRotatingQuestionProps = {
  questionId: number | string;
};

const getQuestion = async ({ questionId }: GetQuestionProps): Promise<AxiosResponse<Question>> => {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('id', questionId)
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Question);
};

const getAllQuestions = async (): Promise<AxiosResponse<Question[]>> => {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return asResponse(data as unknown as Question[]);
};

const getCurrentCheckin = async (): Promise<AxiosResponse<Question[]>> => {
  // Get current questions (not deleted)
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .is('deleted_at', null)
    .order('order_number', { ascending: true });

  if (error) throw error;
  return asResponse(data as unknown as Question[]);
};

const getRotatingQuestions = async (): Promise<AxiosResponse<Question[]>> => {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('question_frequency', 'rotating')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return asResponse(data as unknown as Question[]);
};

const editRotatingQuestion = async ({
  question,
}: EditRotatingQuestionProps): Promise<AxiosResponse<Question>> => {
  const { id, ...fields } = question;
  const updatePayload: Record<string, any> = {};

  if (fields.label !== undefined) updatePayload.label = fields.label;
  if (fields.answerType !== undefined) updatePayload.answer_type = fields.answerType;
  if (fields.isRequired !== undefined) updatePayload.is_required = fields.isRequired;
  if (fields.questionFrequency !== undefined) updatePayload.question_frequency = fields.questionFrequency;
  if (fields.description !== undefined) updatePayload.description = fields.description;
  if (fields.scheduledAt !== undefined) updatePayload.scheduled_at = fields.scheduledAt;

  const { data, error } = await supabase
    .from('questions')
    .update(updatePayload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Question);
};

const createRotatingQuestion = async ({
  question,
}: CreateRotatingQuestionProps): Promise<AxiosResponse<Question>> => {
  const { data, error } = await supabase
    .from('questions')
    .insert({
      label: question.label,
      answer_type: question.answerType,
      is_required: question.isRequired,
      question_frequency: question.questionFrequency,
      description: question.description,
      scheduled_at: question.scheduledAt || null,
    })
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Question);
};

const deleteRotatingQuestion = async ({
  questionId,
}: DeleteRotatingQuestionProps): Promise<AxiosResponse<Question>> => {
  const { data, error } = await supabase
    .from('questions')
    .delete()
    .eq('id', questionId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Question);
};

export default {
  getQuestion,
  getAllQuestions,
  getCurrentCheckin,
  getRotatingQuestions,
  editRotatingQuestion,
  createRotatingQuestion,
  deleteRotatingQuestion,
};
